import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import styled from "styled-components";
import SkeletonPreview from "../../../components/SkeletonPreview";
import { ColorPicker } from '@fluentui/react';
import { RiCheckLine, RiCheckboxCircleFill, RiErrorWarningFill } from "react-icons/ri";
import a11yContrastChecker from "../components/constrastChecker";
import { useBranding } from "./BrandingContext";
import NavigationComponent from "./NavigationComponent";
import BrandingHeader from "./components/brandingHeader";
const Container = styled.div.withConfig({ displayName: "Container", componentId: "-1h3xsrg" }) `
  display: flex;
  flex-direction: column;
  
  &.mode-wizard {
    min-height: calc(100vh - 200px);
  }
`;
const UiContainer = styled.div.withConfig({ displayName: "UiContainer", componentId: "-1w5wg9t" }) `
  display: flex;
  gap: 30px;
  width: 100%;

  > div {
    min-width: 305px;
  }

  h3 {
    color: #3D3A3B;
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 20px;
  }
`;
const ColorPickerContainer = styled.div.withConfig({ displayName: "ColorPickerContainer", componentId: "-plgy10" }) `
  padding: 0;
  border: 2px solid #f7f7f7;
  border-radius: 12px;

  .ms-ColorPicker-panel {
    padding: 14px 14px 20px;
  }

  .ms-TextField-fieldGroup {
    border-color: #E5E8E8;
    border-radius: 8px;
  }

  tr {
    height: 30px;
    font-weight: 600;
  }

  .mode-edit & {
    box-shadow: 0 1px 1px rgba(0,0,0,0.025), 
              0 2px 2px rgba(0,0,0,0.025), 
              0 4px 4px rgba(0,0,0,0.025), 
              0 8px 8px rgba(0,0,0,0.025);
  }
`;
const Cards = styled.div.withConfig({ displayName: "Cards", componentId: "-pdmere" }) `
  display: flex;
  gap: 30px;
`;
const Card = styled.div.withConfig({ displayName: "Card", componentId: "-1qjhepv" }) `
  position: relative;
  max-width: 350px;
  border-radius: 10px;
  border: 2px solid #f2f2f2;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 1px rgba(0,0,0,0.025), 
              0 2px 2px rgba(0,0,0,0.025), 
              0 4px 4px rgba(0,0,0,0.025), 
              0 8px 8px rgba(0,0,0,0.025);

  p {
    margin-block: 0 20px;
  }

  &.selected {
    border-color: #512da8;
  }
`;
const CardHeader = styled.div.withConfig({ displayName: "CardHeader", componentId: "-75qe00" }) `
  padding: 16px;
  background: #f2f2f2;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px 10px 0 0;
`;
const CardBody = styled.div.withConfig({ displayName: "CardBody", componentId: "-cxzdnm" }) `
  padding: 24px;
`;
const PickerToggleContainer = styled.div.withConfig({ displayName: "PickerToggleContainer", componentId: "-1le105" }) `
  display: flex;
  margin-bottom: 20px;

  > div {
    margin-block: 0;
    padding-block: 0;
    max-width: 240px;
  }
`;
const PickerToggle = styled.div.withConfig({ displayName: "PickerToggle", componentId: "-150oug9" }) `
  position: relative;
  width: 60px;
  height: 60px;
  background: conic-gradient(
    from 90deg,
    violet,
    indigo,
    blue,
    green,
    yellow,
    orange,
    red,
    violet
  );
  display: inline-block;
  border-radius: 50%;
  margin-block: 0 10px;
  cursor: pointer;
`;
const PickerToggleInner = styled.div.withConfig({ displayName: "PickerToggleInner", componentId: "-w73d4o" }) `
  position: absolute;
  inset: 5px;
  border-radius: 50%;
  border: 3px solid white;
`;
const SelectedCheck = styled.div.withConfig({ displayName: "SelectedCheck", componentId: "-w0b5w5" }) `
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  background: #512da8;
  border: 3px solid white;
  border-radius: 50%;
  width: 30px;
  aspect-ratio: 1 / 1;
  color: white;
`;
const ConstrastMessage = styled.div.withConfig({ displayName: "ConstrastMessage", componentId: "-rdglw9" }) `
  display: flex;
  max-width: 100%;
  font-size: 14px;
  font-weight: 600;
  padding-block: 15px;
  max-width: 300px;
  transition: all 0.3s ease-in-out;

  svg {
    flex: 0 0 auto;
    font-size: 20px;
    margin: 3px 8px;
  }

  &.success {
    color: #107C10;
  }

  &.warning {
    color: #A4262C;
  }
`;
const A11yMessage = (props) => {
    if (props.isWCAGCompliant === null) {
        return null;
    }
    return props.isWCAGCompliant ? (_jsxs(ConstrastMessage, Object.assign({ className: "success" }, { children: [_jsx(RiCheckboxCircleFill, {}), _jsx("div", { children: "The chosen color complies with the Web Content Accessibility Guidelines usability criteria." })] }))) : (_jsxs(ConstrastMessage, Object.assign({ className: "warning" }, { children: [_jsx(RiErrorWarningFill, {}), _jsx("div", { children: "Insufficient color contrast. Chose a darker shade of color." })] })));
};
const Color = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { selectedTheme, setSelectedTheme } = useBranding();
    const [primaryColor, setPrimaryColor] = React.useState({ str: ((_b = (_a = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _a === void 0 ? void 0 : _a.color) === null || _b === void 0 ? void 0 : _b.primaryColor) ? selectedTheme.themeSettings.color.primaryColor : "#3D3A3B" });
    const [selectedStyle, setSelectedStyle] = React.useState(((_d = (_c = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _c === void 0 ? void 0 : _c.color) === null || _d === void 0 ? void 0 : _d.style) ? selectedTheme.themeSettings.color.style : "intense");
    const [displayPicker, setDisplayPicker] = React.useState(props.wizardMode);
    const [isWCAGCompliant, setIsWCAGCompliant] = React.useState(null);
    const updatePrimaryColor = React.useCallback((ev, colorObj) => setPrimaryColor(colorObj), []);
    React.useEffect(() => {
        if (primaryColor) {
            setTimeout(() => {
                if (selectedStyle === "intense") {
                    setIsWCAGCompliant(a11yContrastChecker(primaryColor.str, "#F7F7F7").levels.aaUi &&
                        a11yContrastChecker(primaryColor.str, "#F7F7F7").levels.aaNormal);
                }
                if (selectedStyle === "soft") {
                    setIsWCAGCompliant(a11yContrastChecker(primaryColor.str, "#F7F7F7").levels.aaUi &&
                        (a11yContrastChecker(primaryColor.str, "#F7F7F7").levels.aaNormal ||
                            a11yContrastChecker(primaryColor.str, "#3D3A3B").levels.aaNormal));
                }
            }, 0);
        }
        setSelectedTheme((prev) => (Object.assign(Object.assign({}, prev), { themeSettings: Object.assign(Object.assign({}, prev === null || prev === void 0 ? void 0 : prev.themeSettings), { color: {
                    primaryColor: primaryColor ? primaryColor.str : "#3D3A3B",
                    style: selectedStyle ? selectedStyle : "intense"
                } }) })));
    }, [primaryColor, selectedStyle]);
    return (_jsxs(Container, Object.assign({ className: props.wizardMode ? "mode-wizard" : "mode-edit" }, { children: [props.wizardMode ? (_jsx(BrandingHeader, { title: "Pick primary color", summary: "Select a primary color and it's application to be applied on theme.", advancedSettings: true })) : null, _jsxs(UiContainer, { children: [_jsxs("div", { children: [props.wizardMode ? (_jsx("h3", { children: "Pick a color" })) : (_jsxs(_Fragment, { children: [_jsx("h3", { children: "Primary Color" }), _jsxs(PickerToggleContainer, { children: [_jsx(PickerToggle, Object.assign({ onClick: () => setDisplayPicker(!displayPicker) }, { children: _jsx(PickerToggleInner, { style: { background: primaryColor ? primaryColor.str : (_e = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _e === void 0 ? void 0 : _e.color.primaryColor } }) })), !props.wizardMode ? (_jsx(A11yMessage, { isWCAGCompliant: isWCAGCompliant })) : null] })] })), displayPicker ? (_jsxs(_Fragment, { children: [_jsx(ColorPickerContainer, { children: _jsx(ColorPicker, { color: primaryColor ? primaryColor.str : (_f = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _f === void 0 ? void 0 : _f.color.primaryColor, alphaType: "none", onChange: updatePrimaryColor }) }), props.wizardMode ? (_jsx(A11yMessage, { isWCAGCompliant: isWCAGCompliant })) : null] })) : null] }), _jsxs("div", { children: [_jsx("h3", { children: "Select a color application" }), _jsxs(Cards, { children: [_jsxs(Card, Object.assign({ onClick: () => setSelectedStyle("intense"), className: selectedStyle === "intense" ? "selected" : "" }, { children: [_jsx(CardHeader, { children: "Intense" }), _jsxs(CardBody, { children: [_jsx("p", { children: "This is how the primary color will be applied in your layout." }), _jsx(SkeletonPreview, { primaryColor: primaryColor ? primaryColor.str : (_g = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _g === void 0 ? void 0 : _g.color.primaryColor, style: "intense" })] }), selectedStyle === "intense" ? (_jsx(SelectedCheck, { children: _jsx(RiCheckLine, {}) })) : null] })), _jsxs(Card, Object.assign({ onClick: () => setSelectedStyle("soft"), className: selectedStyle === "soft" ? "selected" : "" }, { children: [_jsx(CardHeader, { children: "Soft" }), _jsxs(CardBody, { children: [_jsx("p", { children: "This is how the primary color will be applied in your layout." }), _jsx(SkeletonPreview, { primaryColor: primaryColor ? primaryColor.str : (_h = selectedTheme === null || selectedTheme === void 0 ? void 0 : selectedTheme.themeSettings) === null || _h === void 0 ? void 0 : _h.color.primaryColor, style: "soft" })] }), selectedStyle === "soft" ? (_jsx(SelectedCheck, { children: _jsx(RiCheckLine, {}) })) : null] }))] })] })] }), props.wizardMode ? (_jsx(NavigationComponent, { currentStepName: "Color" })) : null] })));
};
export default Color;
